import get from 'lodash/get'
import {F} from '@/ladmin'
import uniqBy from 'lodash/uniqBy'
import uniq from 'lodash/uniq'

class Tab {
  tid // tab ID
  name // tab的名称，一般为route的name，作为搜索name的依据
  label // tab展示文字
  desc // tab说明
  componentName // 缓存组件的名称，由于keepalive的include属性必须使用组件的name，所以此处需要传入name
  to // tab跳转的地址
  persistent // 是否固定tab
  beforeCloseName // 关闭前调用的函数
  notCache // 关闭缓存

  constructor ({tid, label, desc, to, name, componentName, persistent, beforeCloseName, notCache}) {
    if (typeof to === 'string') {
      this.to = to || ''
      // 强制decode成中文，避免存在encode和decode两个版本
      this.to = decodeURI(this.to)
    }
    else {
      console.warn('attribute to cannot be empty')
    }
    this.name = name || ''
    this.label = label || ''
    this.desc = desc || ''
    this.tid = tid || this.to || ''
    this.componentName = componentName || ''
    this.persistent = persistent || false
    this.beforeCloseName = beforeCloseName
    this.notCache = notCache || false
  }

  static fromRoute (route) {
    if (route.meta.singlePage) return
    const matched = route.matched || []
    let componentName = get(matched[Math.min(matched.length - 1, 1)], 'components.default.name')
    // 为获取到名称或组件为component（动态组件）
    if (!componentName || componentName === 'component') {
      // 采用route name
      componentName = route.name
    }
    // if (!componentName) {
    //   console.log('no name', route)
    // }
    let t = {
      name: route.name || '',
      componentName: componentName,
      label: route.meta.label || route.name || route.path,
      to: route.fullPath || route.path,
      persistent: route.meta.persistent || false,
      beforeCloseName: route.meta.beforeCloseName,
      notCache: route.meta.notCache,
      singleton: route.meta.singleton,
    }
    let text = get(route, route.meta.desc) || get(route, route.meta.subText)
    t.desc = text === undefined ? route.meta.desc || route.meta.subText : text

    return new Tab(t)
  }
}

function isPath (t) {
  if (typeof t === 'string' && t) {
    return true
  }
  else if (t instanceof Object && (t.path || t.name)) {
    return true
  }
  else {
    return false
  }
}

class TabList {
  list
  $_current = -1
  closedList = []
  historyList = []
  MAX_HISTORY_LENGTH = 10
  MAX_CLOSED_LENGTH = 10
  STORAGE_KEY = '$RouterTabItems'
  $_router

  constructor (router, array) {
    this.list = array instanceof Array ? [...array.filter(t => t instanceof Tab)] : []
    this.$_router = router
  }

  get current () {
    return this.$_current
  }

  get length () {
    return this.list.length
  }

  get lastTab () {
    // 最后一个是当前路由
    return this.historyList[this.historyList.length - 2]
  }

  get lastCloseTab () {
    return this.closedList[this.closedList.length - 1]
  }

  get currentTab () {
    return this.list[this.current]
  }

  get componentNameList () {
    return uniq(this.list.map(t => !t.notCache && t.componentName).filter(n => !!n))
  }

  get toList () {
    return this.list.map(t => !t.notCache && t.to).filter(key => !!key)
  }

  $_addHistory (...args) {
    // console.log('add history', ...args)
    this.historyList.push(...args)
    const l = this.historyList.length
    const max = this.MAX_HISTORY_LENGTH
    if (l > max) {
      this.historyList = this.historyList.slice(l - max)
    }
  }

  $_addClosed (...args) {
    this.closedList.push(...args)
    const max = this.MAX_CLOSED_LENGTH
    const l = this.closedList.length
    if (l > max) {
      this.closedList = this.closedList.slice(l - max)
    }
  }

  $_findFirstInsertPlace () {
    for (let i = 0; i < this.length; ++i) {
      if (!this.list[i].persistent) return i
    }
    return this.length
  }

  // 获取tab，可以获取到唯一值
  get (i) {
    if (typeof i === 'number') {
      return this.list[i]
    }
    if (typeof i === 'string') {
      return this.list.find(tab => tab.tid === i)
    }
    if (typeof i instanceof Tab) {
      return this.list.find(tab => tab.tid === i.tid)
    }
  }

  // 搜索tab，结果不一定唯一，仅开放接口
  find (fn) {
    return this.list.find(fn)
  }

  indexOf (tab) {
    return this.list.indexOf(tab)
  }

  set (i, opt) {
    // console.log('set', i, opt)
    let tab = this.get(i)
    if (tab) {
      if (opt instanceof Tab) {
        this.list.splice(i, 1, opt)
      }
      else if (opt instanceof Object) {
        let t = new Tab(tab)
        Object.keys(opt).forEach(key => {
          if (t.hasOwnProperty(key)) {
            t[key] = opt[key]
          }
        })
        this.list.splice(i, 1, t)
      }
    }
    this.saveToLocal()
  }

  // 向列表中插入一个tab，如果存在则跳转
  push (tab, force = false) {
    // console.log('push', force ? 'force' : '', tab)
    if (tab instanceof Tab) {
      if (this.currentTab && this.currentTab.tid === tab.tid && !force) return
      let t = this.get(tab.tid)
      if (t) {
        // tab已存在
        this.$_current = this.list.indexOf(t)
      }
      else if (this.current < 0) {
        // tab不存在并且当前tab不存在
        let i = this.$_findFirstInsertPlace()
        this.list.splice(i, 0, tab)
        this.$_current = i
      }
      else {
        // 当前tab存在
        this.list.splice(this.current + 1, 0, tab)
        this.$_current = this.list.indexOf(tab)
      }
      this.$_router.push(tab.to)
      this.$_addHistory(tab)
      this.saveToLocal()
    }
    else if (isPath(tab)) {
      this.$_router.push(tab, (to) => {
        this.push(Tab.fromRoute(to))
      })
    }
  }

  // 替换当前tab
  replace (tab, force = false) {
    if (tab instanceof Tab) {
      if (this.currentTab && this.currentTab.tid === tab.tid && !force) return
      let t = this.get(tab.tid)
      if (t) {
        this.$_current = this.list.indexOf(t)
      }
      else if (this.current < 0) {
        let i = this.$_findFirstInsertPlace()
        this.list.splice(i, 0, tab)
        this.$_current = i
      }
      else {
        this.list.splice(this.current, 1, tab)
      }
      this.$_router.replace(tab.to)
      this.saveToLocal()
    }
    else if (isPath(tab)) {
      this.$_router.replace(tab, (to) => {
        this.replace(Tab.fromRoute(to))
      })
    }
  }

  move (from, to) {
    // console.log('move', from, to)
    if (from >= 0 && to >= 0) {
      // insertIndex是真正插入的地方
      let insertIndex = from > to ? to + 1 : to
      let temp = this.list[from]
      this.list.splice(from, 1)
      this.list.splice(insertIndex, 0, temp)
      // console.log('moved', this.list)
      this.push(this.list[insertIndex])
      this.saveToLocal()
    }
  }

  close (i, next) {
    let tab = this.get(i)
    if (tab) {
      // 不能关闭固定tab
      if (tab.persistent) return

      const oldCurrentTab = this.currentTab
      const isCloseCurrent = this.current === i
      this.list.splice(i, 1)
      this.$_addClosed(tab)

      const lastIndex = this.list.indexOf(this.lastTab)
      let newCurrent = -1
      if (isCloseCurrent && lastIndex > -1) {
        newCurrent = lastIndex
      }
      else if (this.current >= i) {
        // 将current设置到正确的位置
        newCurrent = this.$_current - 1
      }
      else {
        newCurrent = this.$_current
      }

      const newCurrentTab = this.list[newCurrent]

      if (!newCurrentTab) {
        this.$_router.push('/')
      }
      // 指定跳转tab
      else if (next !== undefined) {
        // console.log('close direct to', next)
        this.push(next)
        // 检查是否存在跳转错误并修复
        setTimeout(() => {
          this.check()
        }, 10)
      }
      // 需要路由跳转
      else if (newCurrentTab.tid !== oldCurrentTab.tid) {
        // console.log('close push to', newCurrentTab)
        this.push(newCurrentTab, true)
      }
      else {
        this.$_current = newCurrent
      }
      this.saveToLocal()
    }
  }

  clearAll () {
    // console.log('clearAllTab')
    let currentTab = this.currentTab
    // 固定tab无法关闭
    let persistentList = this.list.filter(t => t.persistent)
    this.$_addClosed(...this.list.filter(t => !t.persistent))
    this.list = persistentList
    // 当前tab是固定tab
    if (this.list.find(t => t.tid === currentTab.tid)) {
      // 修正current，不跳转
      this.$_current = this.list.indexOf(currentTab)
    }
    else if (this.length) {
      // 跳转到第一个
      this.push(this.list[0])
    }
    else {
      // 回到主页
      this.$_router.push('/')
    }
    this.saveToLocal()
  }

  clearOther () {
    let currentTab = this.currentTab || {}
    let reserveList = this.list.filter(t => t.persistent || t.tid === currentTab.tid)
    this.$_addClosed(...this.list.filter(t => t !== !t.persistent && t.tid !== currentTab.tid))
    this.list = reserveList
    this.$_current = this.list.indexOf(currentTab)
    this.saveToLocal()
  }

  readFromLocal (persistent = [], current) {
    // 固定tab在最前
    let results = persistent.map(route => Tab.fromRoute(route))
    // console.log('recover persistent', persistent)
    // 缓存其次
    let localTabs = F.getFromLocal(this.STORAGE_KEY) || []
    results.push(...localTabs.filter(t => !!t).map(t => t instanceof Tab ? t : new Tab(t)))
    // console.log('recovery cache', [...results])
    // 若当前tab不在缓存，放到最后
    let currentTab = Tab.fromRoute(current)
    if (!results.find(tab => tab.to === currentTab.to)) {
      results.push(currentTab)
    }
    // console.log('recovery current', [...results], current)
    results = uniqBy(results, 'tid')
    // this.$L.tab = new TabList(results)
    this.list = results
    // console.log('recovery final', [...results])
  }

  // 修复路由对应错误，如果有的话
  check () {
    const route = this.$_router.app.$route || {}
    let currentTab = this.currentTab
    if (currentTab && currentTab.to === route.fullPath) {
      // tab对应的路由路径没有问题
    }
    else {
      // current存在问题
      let tab = this.find(t => t.to === route.fullPath)
      if (tab) {
        // 当前路由能找到对应tab
        this.$_current = this.list.indexOf(tab)
      }
      else {
        // 当前路由无法对应tab，需要新建
        let newTab = Tab.fromRoute(route)
        let i = this.$_findFirstInsertPlace()
        this.list.splice(i, 0, newTab)
        this.$_current = i
      }
    }
  }

  saveToLocal () {
    // console.log('save', this.list)
    F.saveToLocal(this.STORAGE_KEY, this.list)
  }
}

export {Tab, TabList}
